import type { AnnotationStyle } from 'generated/graphql';
import type { DreMouseEvent, MouseHandlers } from '../../modules/types';
import type { Vector3 } from '@kitware/vtk.js/types';
import {
  optional,
  object,
  literal,
  union,
  string,
  number,
  withDefault,
  array,
  bool,
} from '@recoiljs/refine';
import type { Checker } from '@recoiljs/refine';
import { vec3Checker } from 'utils/refine';
import { Line, Label, Polygon } from '../../Primitives';
import { makeSegmentsFromPoints } from '../../AnnotationTools/utils';
import { useAnnotationColors } from '../helpers/useAnnotationColors';

type PrimitiveRendererProps = {
  id: string;
  cursor: string;
  color?: string;
  onDrag: MouseHandlers['onDrag'];
  onDragEnd: MouseHandlers['onDragEnd'];
  onDragStart: MouseHandlers['onDragStart'];
  onMouseLeave: (arg1: DreMouseEvent) => unknown;
  onMouseOver: (arg1: DreMouseEvent) => unknown;
};

type CommonPrimitiveProps = {
  style?: Readonly<Omit<AnnotationStyle, '__typename'>>;
  visible?: boolean;
  ['data-testid']?: string;
};

type LinePrimitive = Readonly<
  CommonPrimitiveProps & {
    type: 'line';
    start: Vector3;
    end: Vector3;
  }
>;

type LabelPrimitive = Readonly<
  CommonPrimitiveProps & {
    type: 'label';
    position: Vector3;
    offsetDirection?: Vector3;
    offsetDistance?: number;
    label: string;
    alignVertical?: 'top' | 'center' | 'bottom';
    alignHorizontal?: 'left' | 'center' | 'right';
  }
>;

type PolygonPrimitive = Readonly<
  CommonPrimitiveProps & {
    type: 'polygon';
    points: ReadonlyArray<Vector3>;
    fill?: boolean;
  }
>;

export type Primitive = LinePrimitive | LabelPrimitive | PolygonPrimitive;
export type PrimitiveType = 'line' | 'label' | 'polygon';

const annotationStyleChecker = () =>
  optional(
    object({
      color: optional(string()),
      opacity: optional(number()),
    })
  );

function styleToProps(
  style: Nullable<Readonly<Omit<AnnotationStyle, '__typename'>>>,
  color: Nullable<string>,
  defaultColor: string
) {
  const out: {
    color?: string;
    opacity?: number;
  } = {};
  if (style?.color != null) {
    const isDefaultColor = color === defaultColor;
    if (color == null) {
      out.color = style.color;
    } else {
      out.color = isDefaultColor ? style.color : color;
    }
  } else if (color != null) {
    out.color = color;
  }
  if (style?.opacity != null) {
    out.opacity = style.opacity;
  }
  return out;
}

export const primitivesConfig = [
  {
    type: 'line',
    checker: object({
      type: literal('line'),
      visible: withDefault(bool(), false),
      'data-testid': optional(string()),
      style: annotationStyleChecker(),
      start: vec3Checker(),
      end: vec3Checker(),
    }) as Checker<LinePrimitive>,
    renderer: (({ type, start, end, style, id, color, ...props }) => {
      const { defaultColor } = useAnnotationColors();
      return (
        <Line
          id={id}
          start={start}
          end={end}
          {...styleToProps(style, color, defaultColor)}
          {...props}
        />
      );
    }) as React.ComponentType<PrimitiveRendererProps & LinePrimitive>,
  },
  {
    type: 'label',
    checker: object({
      type: literal('label'),
      visible: withDefault(bool(), false),
      'data-testid': optional(string()),
      style: annotationStyleChecker(),
      position: vec3Checker(),
      label: string(),
      alignVertical: withDefault(
        union(literal('top'), literal('center'), literal('bottom')),
        'top'
      ),
      alignHorizontal: withDefault(
        union(literal('left'), literal('center'), literal('right')),
        'left'
      ),
      offsetDirection: withDefault(vec3Checker(), [0, 0, 0]),
      offsetDistance: withDefault(number(), 0),
    }) as Checker<LabelPrimitive>,
    renderer: (({
      type,
      style,
      position,
      label,
      alignVertical,
      alignHorizontal,
      offsetDirection,
      offsetDistance,
      id,
      color,
      ...props
    }) => {
      const { defaultColor } = useAnnotationColors();
      return (
        <Label
          id={id}
          anchorPoint={position}
          topLeftBoundingBoxPoint={position}
          children={label}
          alignVertical={alignVertical}
          alignHorizontal={alignHorizontal}
          offsetDirection={offsetDirection}
          offsetDistance={offsetDistance}
          {...styleToProps(style, color, defaultColor)}
          {...props}
        />
      );
    }) as React.ComponentType<PrimitiveRendererProps & LabelPrimitive>,
  },
  {
    type: 'polygon',
    checker: object({
      type: literal('polygon'),
      visible: withDefault(bool(), false),
      'data-testid': optional(string()),
      fill: withDefault(bool(), false),
      style: annotationStyleChecker(),
      points: array(vec3Checker()),
    }) as Checker<PolygonPrimitive>,
    renderer: (({ type, points, style, id, color, fill = false, ...props }) => {
      const { defaultColor } = useAnnotationColors();
      return (
        <Polygon
          id={id}
          fill={fill ? 'polys' : 'lines'}
          segments={makeSegmentsFromPoints(points)}
          {...styleToProps(style, color, defaultColor)}
          {...props}
        />
      );
    }) as React.ComponentType<PrimitiveRendererProps & PolygonPrimitive>,
  },
] as const;

export const primitiveChecker: Checker<Primitive> = union(
  ...primitivesConfig.map(({ checker }) => checker)
);
